import { ArrowLeftStroke, FloppyDisk1Stroke } from '@lineiconshq/free-icons';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AritoActionButton } from '@/components/custom/arito/action-button';
import AritoActionBar from '@/components/custom/arito/action-bar';
import type { TabItem } from '../shared-types';
import { AritoFormContext } from './context';
import { FormMode } from '@/types/form';
import { cn } from '@/lib/utils';

export { AritoFormContext } from './context';
export type { TabItem } from '../shared-types';

export interface AritoFormProps<TFormData extends Record<string, any>> {
  mode?: FormMode;
  hasAritoActionBar?: boolean;
  title?: string;
  subTitle?: string;
  initialData?: Partial<TFormData>;
  onSubmit?: (data: TFormData) => void | Promise<void>;
  schema?: z.ZodType<any>;
  headerFields?: ReactNode | TabItem[];
  tabs?: ReactNode | TabItem[];
  onClose?: () => void;
  from?: ReactNode;
  headerBar?: ReactNode;
  bottomBar?: ReactNode;
  actionButtons?: ReactNode;
  className?: string;
  classNameHeaderBar?: string;
}

const tabListClassName = 'flex h-8 w-full items-center overflow-x-auto border-b border-gray-200';
const headerTabClassName =
  'min-h-8 shrink-0 border-b border-transparent px-4 py-1.5 text-sm font-black normal-case text-gray-700 outline-none transition-colors hover:text-blue-600 focus-visible:ring-1 focus-visible:ring-blue-500';
const mainTabClassName =
  'min-h-8 shrink-0 border-b border-transparent px-4 py-0.5 text-[11px] font-black normal-case text-gray-700 outline-none transition-colors hover:text-[#023e8a] focus-visible:ring-1 focus-visible:ring-[#023e8a]';

export const AritoForm = <TFormData extends Record<string, any>>({
  mode = 'edit',
  title,
  hasAritoActionBar = true,
  subTitle,
  initialData,
  onSubmit,
  schema,
  headerFields,
  tabs,
  onClose,
  headerBar,
  bottomBar,
  actionButtons,
  className,
  classNameHeaderBar
}: AritoFormProps<TFormData>) => {
  const [activeTab, setActiveTab] = useState<string>(Array.isArray(tabs) ? tabs[0]?.id || '' : '');
  const [activeHeaderTab, setActiveHeaderTab] = useState<string>(
    Array.isArray(headerFields) ? headerFields[0]?.id || '' : ''
  );

  const methods = useForm<TFormData>({
    defaultValues: initialData as DefaultValues<TFormData>,
    mode: 'onSubmit',
    ...(schema && { resolver: zodResolver(schema) })
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = methods;

  useEffect(() => {
    if (initialData) {
      reset(initialData as DefaultValues<TFormData>);
    }
  }, [initialData, reset]);

  const isViewMode = mode === 'view';
  const headerHasTabs = Array.isArray(headerFields);
  const tabsHasTabs = Array.isArray(tabs);
  const contextValue = useMemo(() => ({ control, errors, isViewMode }), [control, errors, isViewMode]);
  const headerTabIds = useMemo(
    () => (headerHasTabs ? (headerFields as TabItem[]).map(tab => tab.id).join('|') : ''),
    [headerFields, headerHasTabs]
  );
  const activeHeaderTabItem = useMemo(
    () => (headerHasTabs ? (headerFields as TabItem[]).find(tab => tab.id === activeHeaderTab) : undefined),
    [activeHeaderTab, headerFields, headerHasTabs]
  );
  const tabIds = useMemo(
    () => (tabsHasTabs ? (tabs as TabItem[]).map(tab => tab.id).join('|') : ''),
    [tabs, tabsHasTabs]
  );
  const activeTabItem = useMemo(
    () => (tabsHasTabs ? (tabs as TabItem[]).find(tab => tab.id === activeTab) : undefined),
    [activeTab, tabs, tabsHasTabs]
  );

  useEffect(() => {
    if (headerHasTabs) {
      const headerTabs = headerFields as TabItem[];
      setActiveHeaderTab(current => (headerTabs.some(tab => tab.id === current) ? current : headerTabs[0]?.id || ''));
    }
  }, [headerFields, headerHasTabs, headerTabIds]);

  useEffect(() => {
    if (tabsHasTabs) {
      const formTabs = tabs as TabItem[];
      setActiveTab(current => (formTabs.some(tab => tab.id === current) ? current : formTabs[0]?.id || ''));
    }
  }, [tabs, tabsHasTabs, tabIds]);

  const handleClose = () => {
    onClose?.();
  };

  const onFormSubmit = (data: TFormData) => {
    return onSubmit?.(data);
  };

  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue);
  };

  const handleHeaderTabChange = (newValue: string) => {
    setActiveHeaderTab(newValue);
  };

  return (
    <FormProvider {...methods}>
      <AritoFormContext.Provider value={contextValue}>
        <div className={cn('relative flex w-full flex-col overflow-auto bg-white', className)}>
          <div
            className='flex flex-1 flex-col bg-white'
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <form
              onSubmit={handleSubmit(
                data => {
                  console.log('AritoForm submit success:', data);
                  onFormSubmit(data);
                },
                err => {
                  console.log('AritoForm validation errors:', err);
                }
              )}
              className={'flex flex-1 flex-col overflow-hidden'}
            >
              {hasAritoActionBar && (
                <AritoActionBar
                  inForm={true}
                  className='border-b border-gray-300'
                  titleComponent={
                    <div className='flex flex-col items-center justify-center lg:items-start lg:justify-start'>
                      <h1 className='text-[length:var(--erp-action-bar-title-size)] font-bold'>
                        {title || (mode === 'add' && 'Thêm')}
                      </h1>
                      <h2 className='text-xs text-gray-500'>{subTitle}</h2>
                    </div>
                  }
                >
                  {actionButtons || (
                    <>
                      {!isViewMode && (
                        <AritoActionButton title='Lưu' icon={FloppyDisk1Stroke} variant='secondary' type='submit' />
                      )}
                      {
                        <AritoActionButton
                          title='Đóng'
                          icon={ArrowLeftStroke}
                          variant='destructive'
                          onClick={handleClose}
                        />
                      }
                    </>
                  )}
                </AritoActionBar>
              )}

              {headerBar && (
                <div className={cn('w-full border-b border-gray-300', classNameHeaderBar)}>{headerBar}</div>
              )}

              {headerFields && (
                <div className='border-none'>
                  {headerHasTabs && (
                    <>
                      <div className={tabListClassName} role='tablist'>
                        {(headerFields as TabItem[]).map(tab => (
                          <button
                            key={tab.id}
                            type='button'
                            role='tab'
                            aria-selected={activeHeaderTab === tab.id}
                            className={cn(
                              headerTabClassName,
                              activeHeaderTab === tab.id && 'border-[#023e8a] text-blue-600'
                            )}
                            onClick={() => handleHeaderTabChange(tab.id)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Header Tab Content */}
                      <div>{activeHeaderTabItem?.component}</div>
                    </>
                  )}

                  {!headerHasTabs && <div>{headerFields}</div>}
                </div>
              )}

              <div className='flex flex-1 flex-col overflow-hidden'>
                {tabs && (
                  <>
                    {tabsHasTabs && (
                      <>
                        <div className={tabListClassName} role='tablist'>
                          {(tabs as TabItem[]).map(tab => (
                            <button
                              key={tab.id}
                              type='button'
                              role='tab'
                              aria-selected={activeTab === tab.id}
                              className={cn(
                                mainTabClassName,
                                activeTab === tab.id && 'border-[#023e8a] text-[#023e8a]'
                              )}
                              onClick={() => handleTabChange(tab.id)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        {/* Tab Content */}
                        <div className='flex flex-1 flex-col overflow-hidden'>
                          {(tabs as TabItem[]).map(tab => (
                            <div key={tab.id} className={cn(activeTab === tab.id ? 'flex flex-1 flex-col' : 'hidden')}>
                              <div className='flex flex-1 flex-col overflow-hidden'>{tab.component}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {!tabsHasTabs && <div>{tabs}</div>}
                  </>
                )}
              </div>

              {bottomBar && (
                <div
                  className={cn('fixed bottom-0 left-0 right-0 z-10 w-full border-t border-gray-200 bg-white')}
                  data-arito-bottom-bar
                >
                  {bottomBar}
                </div>
              )}
            </form>
          </div>
        </div>
      </AritoFormContext.Provider>
    </FormProvider>
  );
};
